import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Check, Circle } from 'lucide-react';
import { PASSWORD_CRITERIA, PASSWORD_MIN_LENGTH } from 'utils/passwordUtils';

interface PasswordRuleChecklistProps {
  password: string;
  id?: string;
}

type CriterionKey = (typeof PASSWORD_CRITERIA)[number]['key'];

// One literal key per criterion, as the i18n static guard requires.
const criterionLabel = (t: TFunction, key: CriterionKey): string => {
  switch (key) {
    case 'length':
      return t('At least {{min}} characters', { min: PASSWORD_MIN_LENGTH });
    case 'upper':
      return t('One uppercase letter');
    case 'lower':
      return t('One lowercase letter');
    case 'digit':
      return t('One digit');
    default:
      return t('One special character (e.g. ! @ # $ %)');
  }
};

/** The password rule as a live list: each criterion ticks once the typed value meets it. */
const PasswordRuleChecklist = ({ password, id }: PasswordRuleChecklistProps) => {
  const { t } = useTranslation();

  return (
    <ul id={id} className="space-y-0.5 text-xs text-muted-foreground">
      {PASSWORD_CRITERIA.map(({ key, test }) => {
        const met = test(password);
        return (
          <li key={key} className="flex items-center gap-1.5">
            {met ? (
              <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
            ) : (
              <Circle className="h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden="true" />
            )}
            <span>
              {criterionLabel(t, key)}
              <span className="sr-only"> ({met ? t('met') : t('not met')})</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default PasswordRuleChecklist;
